import React from 'react';
import { useActiveVehicle } from 'containers/pages/CANpark/context/ActiveVehicleProvider';
import CallIcon from '@mui/icons-material/Call';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
	Box,
	Typography,
	IconButton,
	Tooltip,
	Link,
	Button,
} from '@mui/material';
import { useNotificationProvider } from 'hooks';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DoneIcon from '@mui/icons-material/Done';

interface PhoneBoxProps {
	text: string;
	phoneNumber: string;
}
const PhoneBox = ({ text, phoneNumber }: PhoneBoxProps) => {
	const { notifySuccess } = useNotificationProvider();

	return (
		<>
			<Typography noWrap>{text}</Typography>
			<Box gap={2}>
				<Tooltip title="Call">
					<Link href={`tel:${phoneNumber}>`}>
						<IconButton size="large">
							<CallIcon />
						</IconButton>
					</Link>
				</Tooltip>

				<Tooltip title="Copy">
					<IconButton
						size="large"
						onClick={() => {
							navigator.clipboard.writeText(phoneNumber);
							notifySuccess('Copied to clipboard');
						}}
					>
						<ContentCopyIcon />
					</IconButton>
				</Tooltip>
			</Box>
		</>
	);
};

const ActiveVehicleDrawerTowInfoStage = () => {
	const { parkingLocation, closeDrawer, goTo } = useActiveVehicle();

	if (!parkingLocation)
		throw new Error(
			'No parking location found in ActiveVehicleDrawerTowInfoStage',
		);

	const { towingCompanyName, towingCompanyNumber, towInstructions } =
		parkingLocation;
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
			}}
		>
			<Box
				data-tour="ticket-tow-info"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					maxWidth: 600,
					width: '100%',
					gap: 2,
					px: 2,
					pb: 2,
				}}
			>
				<Typography variant="h6" fontWeight={500} color="primary">
					Towing Instructions
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Use these details to coordinate the tow with the contracted
					provider.
				</Typography>

				<Box>
					<Typography
						variant="caption"
						color="text.secondary"
						fontStyle="italic"
						component="div"
					>
						Company Name
					</Typography>
					<Typography variant="body2">
						{towingCompanyName || 'Not provided'}
					</Typography>
				</Box>

				<Box>
					<Typography
						variant="caption"
						color="text.secondary"
						fontStyle="italic"
						component="div"
					>
						Company Number
					</Typography>
					<Box
						display="flex"
						alignItems="center"
						gap={1}
						flexWrap="wrap"
					>
						{towingCompanyNumber ? (
							<PhoneBox
								text={towingCompanyNumber}
								phoneNumber={towingCompanyNumber}
							/>
						) : (
							<Typography variant="body2">
								Not provided
							</Typography>
						)}
					</Box>
				</Box>

				<Box>
					<Typography
						variant="caption"
						color="text.secondary"
						fontStyle="italic"
						component="div"
					>
						Instructions
					</Typography>
					<Typography variant="body2">
						{towInstructions ||
							'No additional instructions provided'}
					</Typography>
				</Box>

				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					gap={2}
					sx={{ mt: 1 }}
				>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => goTo('ticket')}
						startIcon={<ArrowBackIosNewIcon />}
					>
						Back
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={closeDrawer}
						endIcon={<DoneIcon />}
						sx={{
							borderRadius: 2,
							fontWeight: 500,
							textTransform: 'none',
						}}
					>
						Close
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export default ActiveVehicleDrawerTowInfoStage;
